import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import Card from '../Card'
import I18n from '../../i18n'
import useFilter from '../../lib/use-filter'
import useLocation from '../../lib/use-location'
import useModalState from '../../lib/use-modal-state'
import useAuthorization from '../../lib/use-authorization'
import CompareIcon from '../LocationDetail/CompareIcon'
import FilterButton from '../LocationDetail/FilterButton'
import MapButton from '../LocationDetail/MapButton'
import SingleSelect from '../forms/SingleSelect'

import {
  findActiveTimerangeItem,
  getTimerangeSelectorItems
} from '../LocationDetail/lib/predefinedFilters'

function MobileControlSheetNative({ hasMap }) {
  const [_activeModal, { openModal, modals }] = useModalState()
  const { location } = useLocation()
  const { permissions } = useAuthorization()
  const [filter, dispatchFilter] = useFilter()

  function compareButtonText() {
    switch (filter.compareType) {
      case 'multiLocation': {
        return I18n.t('location.comparison.multiLocation')
      }
      case 'timerange':
        return I18n.helpers.dateRange(filter.compareFrom, filter.compareTo)
      case 'average':
        return I18n.t('location.comparison.average')
      default:
        return ''
    }
  }

  function getInitialTimerange() {
    try {
      // set 'today' as default timerange if no filter is set
      return getTimerangeSelectorItems(
        location,
        permissions.earliestAccessibleDate,
        filter
      )[1].options[0]
    } catch {
      return findActiveTimerangeItem(
        filter,
        getTimerangeSelectorItems(
          location,
          permissions.earliestAccessibleDate,
          filter
        ),
        location.metadata.latestMeasurementAt
      )
    }
  }

  const [timerange, setTimerange] = useState(getInitialTimerange())

  const NativeControlSheetHandlers = {
    handleTimeRangeChange: item => {
      setTimerange(item)
      if (item.humanReadableKey === 'customDateRange') {
        openModal(modals.timerange)
        return
      }
      dispatchFilter({ type: 'set/timerange', from: item.from, to: item.to })
    }
  }

  return (
    <Wrapper rounded shy>
      <div css={{ display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <p
            css={{
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              marginBottom: '0.25rem'
            }}
          >
            {location.city}
          </p>
          <p
            css={{
              fontWeight: 'bold',
              fontSize: '1rem',
              marginBottom: '2rem'
            }}
          >
            {location.name}
          </p>
        </div>
        {hasMap && <MapButton onClick={() => openModal(modals.map)} />}
      </div>

      <SelectWrapper data-test-id="timerange_selector">
        <SingleSelect
          options={getTimerangeSelectorItems(
            location,
            permissions.earliestAccessibleDate,
            filter
          )}
          onChange={NativeControlSheetHandlers.handleTimeRangeChange}
          value={timerange}
          variant={'SECONDARY'}
        />
      </SelectWrapper>

      <FilterButton
        onClick={() => {
          openModal(modals.comparison)
        }}
        icon={<CompareIcon type={filter.compareType} />}
      >
        <FilterButton.TopLine>
          {I18n.t('location.comparison.label')}
        </FilterButton.TopLine>
        <FilterButton.Title>{compareButtonText()}</FilterButton.Title>
      </FilterButton>
    </Wrapper>
  )
}

MobileControlSheetNative.propTypes = {
  hasMap: PropTypes.bool
}

const Wrapper = styled(Card)`
  width: 100%;
  padding: 1rem;
`

const SelectWrapper = styled.div`
  min-width: 16rem;
  padding-bottom: 0.9rem;
  margin-left: 0;
  height: 2.5rem;

  @media (min-width: 1142px) {
    margin-left: 1rem;
  }
`

export default MobileControlSheetNative
